import type { Env } from '../index';

export interface ProviderResult {
  text: string;
  provider: string;
  model?: string;
}

// Gemini 3.x family uses -preview suffix in v1beta REST.
// Primary: gemini-3.1-flash-lite-preview (500 RPD free pool).
const GEMINI_MODELS = [
  'gemini-3.1-flash-lite-preview', // ★ primary — 500 RPD free pool
  'gemini-3-flash-preview', // separate pool
  'gemini-2.5-flash-lite', // confirmed working in v1beta
  'gemini-2.0-flash-lite',
  'gemini-2.0-flash',
  'gemini-1.5-flash',
  'gemini-2.5-flash', // last among flashes (limit-prone)
  'gemini-3.1-pro-preview', // pro tier last — tiny free quota
  'gemini-3-pro-preview',
  'gemini-2.5-pro',
];

const GEMINI_SKIP_STATUSES = new Set([400, 404, 429, 503]);

async function fetchWithRetry(
  url: string,
  init: RequestInit,
  retries = 2,
  baseMs = 2000,
): Promise<Response> {
  let r = await fetch(url, init);
  let attempt = 0;
  // Only retry on 503 (transient overload). 429 means daily quota exhausted —
  // backoff won't recover in 6s, just delays the chain falling through to the
  // next provider. Per-provider model loops still skip 429 to the next model.
  while (r.status === 503 && attempt < retries) {
    // Abort listeners on an already-aborted signal don't fire, so check first.
    if (init.signal?.aborted) throw new DOMException('aborted', 'AbortError');
    // Abortable sleep: if the caller's signal fires during the backoff,
    // reject so the retry chain unwinds instead of running unkillable for ~60s.
    await new Promise<void>((resolve, reject) => {
      const t = setTimeout(resolve, baseMs * 2 ** attempt);
      init.signal?.addEventListener(
        'abort',
        () => {
          clearTimeout(t);
          reject(new DOMException('aborted', 'AbortError'));
        },
        { once: true },
      );
    });
    r = await fetch(url, init);
    attempt++;
  }
  return r;
}

/** Discover Gemini models the key actually has access to (auto-fallback). */
async function discoverGeminiModels(key: string): Promise<string[]> {
  try {
    const r = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models?key=${encodeURIComponent(key)}`,
    );
    if (!r.ok) return [];
    const j = (await r.json()) as {
      models?: { name?: string; supportedGenerationMethods?: string[] }[];
    };
    return (j.models ?? [])
      .filter((m) => m.supportedGenerationMethods?.includes('generateContent'))
      .map((m) => (m.name ?? '').replace('models/', ''))
      .filter(Boolean);
  } catch {
    return [];
  }
}

export async function askGemini(key: string, prompt: string): Promise<ProviderResult> {
  const tried = new Set<string>();
  let lastErr: Error | null = null;

  const callModel = async (model: string): Promise<string | null> => {
    tried.add(model);
    const r = await fetchWithRetry(
      `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${encodeURIComponent(key)}`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] }),
      },
    );
    if (!r.ok) {
      lastErr = new Error(`gemini-http-${r.status}`);
      if (GEMINI_SKIP_STATUSES.has(r.status)) return null;
      throw lastErr;
    }
    const j = (await r.json()) as { candidates?: { content?: { parts?: { text?: string }[] } }[] };
    const text = j.candidates?.[0]?.content?.parts?.[0]?.text ?? '';
    return text;
  };

  // 1. Try hardcoded curated chain first (priority order)
  for (const model of GEMINI_MODELS) {
    try {
      const text = await callModel(model);
      if (text?.trim()) return { text, provider: 'gemini', model };
    } catch (e) {
      lastErr = e as Error;
    }
  }

  // 2. All hardcoded models failed — auto-discover what the key has access to
  const discovered = await discoverGeminiModels(key);
  const fresh = discovered.filter((m) => !tried.has(m));
  for (const model of fresh) {
    try {
      const text = await callModel(model);
      if (text?.trim()) return { text, provider: 'gemini', model };
    } catch (e) {
      lastErr = e as Error;
    }
  }

  throw lastErr ?? new Error('gemini-all-models-exhausted');
}

// Shared OpenAI-compatible chat-completions caller. Used by askGroq and
// askVolcengine — both speak the same wire format (bearer auth + chat
// completions schema). Iterates models, skips 4xx that mean "this model id
// won't work with this key" so a sibling model in the list can answer.
async function askOpenAICompat(
  url: string,
  key: string,
  prompt: string,
  models: readonly string[],
  provider: 'groq' | 'volcengine',
): Promise<ProviderResult> {
  let lastErr: Error | null = null;
  for (const model of models) {
    try {
      const r = await fetchWithRetry(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${key}` },
        body: JSON.stringify({
          model,
          max_tokens: 800,
          messages: [{ role: 'user', content: prompt }],
        }),
      });
      if (!r.ok) {
        lastErr = new Error(`${provider}-http-${r.status}`);
        if ([400, 404, 429].includes(r.status)) continue;
        throw lastErr;
      }
      const j = (await r.json()) as { choices?: { message?: { content?: string } }[] };
      const text = j.choices?.[0]?.message?.content ?? '';
      if (text.trim()) return { text, provider, model };
    } catch (e) {
      lastErr = e as Error;
    }
  }
  throw lastErr ?? new Error(`${provider}-all-models-exhausted`);
}

export async function askGroq(key: string, prompt: string): Promise<ProviderResult> {
  return askOpenAICompat(
    'https://api.groq.com/openai/v1/chat/completions',
    key,
    prompt,
    ['llama-3.3-70b-versatile', 'llama-3.1-8b-instant'],
    'groq',
  );
}

export async function askOpenAI(key: string, prompt: string): Promise<ProviderResult> {
  const r = await fetchWithRetry('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${key}` },
    body: JSON.stringify({
      model: 'gpt-4o-mini',
      max_tokens: 800,
      messages: [{ role: 'user', content: prompt }],
    }),
  });
  if (!r.ok) throw new Error(`openai-http-${r.status}`);
  const j = (await r.json()) as { choices?: { message?: { content?: string } }[] };
  const text = j.choices?.[0]?.message?.content ?? '';
  return { text, provider: 'openai', model: 'gpt-4o-mini' };
}

export async function askClaude(key: string, prompt: string): Promise<ProviderResult> {
  // Fallback chain so a sunset/retired model name doesn't kill all Claude calls
  // until code ships. Order: current → cheaper current → previous-gen anchor.
  const models = ['claude-sonnet-4-6', 'claude-haiku-4-5-20251001', 'claude-3-5-sonnet-latest'];
  let lastErr: Error | null = null;
  for (const model of models) {
    try {
      const r = await fetchWithRetry('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-api-key': key,
          'anthropic-version': '2023-06-01',
        },
        body: JSON.stringify({
          model,
          max_tokens: 800,
          messages: [{ role: 'user', content: prompt }],
        }),
      });
      if (!r.ok) {
        lastErr = new Error(`claude-http-${r.status}`);
        if ([400, 404, 429].includes(r.status)) continue;
        throw lastErr;
      }
      const j = (await r.json()) as { content?: { text?: string }[] };
      const text = j.content?.[0]?.text ?? '';
      if (text.trim()) return { text, provider: 'claude', model };
    } catch (e) {
      lastErr = e as Error;
    }
  }
  throw lastErr ?? new Error('claude-all-models-exhausted');
}

// Volcengine Ark (ByteDance) — OpenAI-compatible chat-completions endpoint hosted
// in cn-beijing. Reachable from inside mainland China (no GFW issues), and Doubao
// has strong Mandarin training. Volcengine requires dated model snapshots, not
// bare family names. Try Seed 2.0 lite first; fall back to Seed 1.6 lite if
// the newer snapshot isn't enabled on the account.
const VOLCENGINE_MODELS = [
  'doubao-seed-2-0-lite-260215', // newest, speed-optimized
  'doubao-seed-1-6-lite-251015', // 2025-10-15 fallback — broader default-access
];

export async function askVolcengine(key: string, prompt: string): Promise<ProviderResult> {
  return askOpenAICompat(
    'https://ark.cn-beijing.volces.com/api/v3/chat/completions',
    key,
    prompt,
    VOLCENGINE_MODELS,
    'volcengine',
  );
}

/**
 * Cross-provider fallback. The chain order depends on the requesting client's
 * country: from mainland China (CN) we try Volcengine first since Gemini /
 * OpenAI / Claude / Groq are all GFW-blocked. Elsewhere we keep the original
 * chain (Gemini → Groq → OpenAI → Claude) and fall through to Volcengine last.
 */
export async function aiProxy(prompt: string, env: Env, country?: string): Promise<ProviderResult> {
  const isCN = country === 'CN';
  const chain: Array<[string | undefined, (k: string, p: string) => Promise<ProviderResult>]> = isCN
    ? [
        [env.VOLCENGINE_API_KEY, askVolcengine],
        [env.GEMINI_API_KEY, askGemini],
        [env.GROQ_API_KEY, askGroq],
        [env.OPENAI_API_KEY, askOpenAI],
        [env.CLAUDE_API_KEY, askClaude],
      ]
    : [
        [env.GEMINI_API_KEY, askGemini],
        [env.GROQ_API_KEY, askGroq],
        [env.OPENAI_API_KEY, askOpenAI],
        [env.CLAUDE_API_KEY, askClaude],
        [env.VOLCENGINE_API_KEY, askVolcengine],
      ];
  let lastErr: Error | null = null;
  for (const [key, fn] of chain) {
    if (!key) continue;
    try {
      const res = await fn(key, prompt);
      if (res.text.trim()) return res;
    } catch (e) {
      console.warn('provider failed:', (e as Error).message);
      lastErr = e as Error;
    }
  }
  throw lastErr ?? new Error('no provider configured');
}
